'use client';

import { Suspense, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import toast from 'react-hot-toast';
import { authApi } from '../../lib/api';
import { ROUTES } from '../../lib/constants';
import { AuthBackground } from '../../components/auth/AuthBackground';
import { UI } from '../../lib/constants';
import logo from '../../assets/logo.svg';

function ForgotPasswordForm() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      await authApi.forgotPassword({ email });
      setSubmitted(true);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(Array.isArray(msg) ? msg.join(', ') : (msg ?? 'Something went wrong'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex h-full items-center justify-center px-4" style={{ backgroundColor: 'var(--color-bg)' }}>
      <AuthBackground />
      <div className="relative w-full max-w-md">
        {/* App name */}
        <div className="mb-10 flex flex-col items-center gap-2">
          <Link href="/home" className="flex flex-col items-center gap-2">
            <Image src={logo} alt="Apporb" width={48} height={48} className="rounded-2xl" />
            <span className="text-sm font-semibold tracking-widest uppercase" style={{ color: 'var(--color-primary)' }}>
              {UI.appName}
            </span>
          </Link>
        </div>

        {submitted ? (
          /* Success state */
          <div className="text-center">
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5"
              style={{ background: 'linear-gradient(135deg, #e8f0fe, #d3e2fd)' }}
            >
              <svg className="w-7 h-7 text-[#0b57d0]" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
              </svg>
            </div>
            <h1 className="text-3xl font-light text-gray-800 mb-3" style={{ fontFamily: 'Georgia, serif' }}>
              Check your inbox
            </h1>
            <p className="text-sm text-gray-400 mb-8 leading-relaxed">
              If <strong className="text-gray-600">{email}</strong> is registered, you'll receive a reset link shortly.
              The link expires in 1 hour.
            </p>
            <Link
              href={ROUTES.login}
              className="text-sm font-medium hover:underline"
              style={{ color: 'var(--color-primary)' }}
            >
              ← Back to sign in
            </Link>
          </div>
        ) : (
          /* Form state */
          <>
            <div className="text-center mb-8">
              <h1 className="text-4xl font-light text-gray-800" style={{ fontFamily: 'Georgia, serif' }}>
                Forgot password?
              </h1>
              <p className="text-sm text-gray-400 mt-2">
                Enter your email and we'll send you a reset link.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="border-b border-gray-300 pb-1 focus-within:border-[#0b57d0] transition-colors">
                <input
                  id="email"
                  type="email"
                  required
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-transparent text-sm text-gray-800 placeholder-gray-400 outline-none py-1"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 rounded-full text-sm font-semibold text-white transition-opacity disabled:opacity-60"
                style={{ backgroundColor: 'var(--color-primary)' }}
              >
                {loading ? 'Sending…' : 'Send reset link'}
              </button>
            </form>

            <p className="text-center text-sm text-gray-400 mt-6">
              <Link href={ROUTES.login} className="font-medium hover:underline" style={{ color: 'var(--color-primary)' }}>
                Back to sign in
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export default function ForgotPasswordPage() {
  return (
    <Suspense>
      <ForgotPasswordForm />
    </Suspense>
  );
}
